import * as React from "react";
import { useMutation } from "convex/react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  ComboboxSelect,
  type ComboboxOption,
} from "@/components/ui/combobox-select";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/convexApi";
import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import { parseAmountInput, roundToCents } from "../bill-shared";

export interface AllocationRowData {
  _id: string;
  glAccountId: string;
  memo: string;
  amount: number;
  position: number;
  glAccountName?: string;
  glAccountActive: boolean;
}

export interface GlAccountOption {
  _id: string;
  qboAccountName: string;
  label: string;
  active: boolean;
}

export interface SuggestedAllocationRow {
  glAccountId?: string;
  glAccountName: string;
  memo: string;
  amount?: number;
  confidence: "high" | "medium" | "low";
}

interface EditableRow {
  key: number;
  glAccountId: string;
  memo: string;
  amount: string;
}

let nextRowKey = 1;

function toEditableRows(allocations: AllocationRowData[]): EditableRow[] {
  return allocations.map((allocation) => ({
    key: nextRowKey++,
    glAccountId: allocation.glAccountId,
    memo: allocation.memo,
    amount: String(allocation.amount),
  }));
}

/**
 * Content signature used for dirty-detection: compares what the rows *mean*
 * (amounts numerically normalized) so "5" and "5.00" are the same edit state.
 */
function rowsSignature(rows: EditableRow[]): string {
  return rows
    .map(
      (row) =>
        `${row.glAccountId}|${row.memo}|${parseAmountInput(row.amount) ?? row.amount}`,
    )
    .join("\n");
}

export function AllocationsEditor({
  authUserId,
  billId,
  billRevision,
  allocations,
  glAccounts,
  totalAmount,
  canEdit,
  suggestedAllocations = [],
  vendorDefaultGlAccountId,
  onDirtyChange,
}: {
  authUserId: string | undefined;
  billId: string;
  billRevision: number;
  allocations: AllocationRowData[];
  glAccounts: GlAccountOption[] | undefined;
  totalAmount: number | undefined;
  canEdit: boolean;
  suggestedAllocations?: SuggestedAllocationRow[];
  /** The selected vendor's usual GL account; prefills the first row only. */
  vendorDefaultGlAccountId?: string;
  /** Reports unsaved-edit state so the page can guard queue navigation. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const setAllocations = useMutation(api.accountingBills.setAllocations);
  const [rows, setRows] = React.useState<EditableRow[]>(() =>
    toEditableRows(allocations),
  );
  const [isSaving, setIsSaving] = React.useState(false);
  const [invalidKeys, setInvalidKeys] = React.useState<Set<number>>(
    () => new Set(),
  );
  const [conflict, setConflict] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Signature of the last server state we seeded from; rows differing from it
  // means the user has unsaved edits.
  const seedSignatureRef = React.useRef(rowsSignature(toEditableRows(allocations)));
  const seededBillIdRef = React.useRef(billId);

  const dirty = rowsSignature(rows) !== seedSignatureRef.current;
  const dirtyRef = React.useRef(dirty);
  dirtyRef.current = dirty;

  // A non-editable (locked) bill never reports dirty: its read-only view has
  // already discarded local edits, so they must not block queue navigation.
  React.useEffect(() => {
    onDirtyChange?.(canEdit && dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, canEdit, onDirtyChange]);

  const applySeed = React.useCallback(
    (serverAllocations: AllocationRowData[]) => {
      const seeded = toEditableRows(serverAllocations);
      setRows(seeded);
      seedSignatureRef.current = rowsSignature(seeded);
      setInvalidKeys(new Set());
      setConflict(false);
    },
    [],
  );

  // Re-seed when the server revision moves — but never clobber unsaved edits:
  // with local edits in flight we surface a conflict notice instead.
  React.useEffect(() => {
    if (seededBillIdRef.current !== billId) {
      seededBillIdRef.current = billId;
      applySeed(allocations);
      return;
    }
    if (!dirtyRef.current) {
      applySeed(allocations);
    } else {
      setConflict(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billId, billRevision]);

  const updateRow = (key: number, patch: Partial<EditableRow>) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
    setInvalidKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const sum = roundToCents(
    rows.reduce((total, row) => total + (parseAmountInput(row.amount) ?? 0), 0),
  );
  const delta =
    totalAmount !== undefined ? roundToCents(sum - totalAmount) : undefined;
  const balanced = delta !== undefined && Math.abs(delta) < 0.005;

  const addRow = React.useCallback(() => {
    const key = nextRowKey++;
    setRows((prev) => {
      // Prefill the unallocated remainder so the common single-split and
      // last-line cases need no typing (Excel-style).
      const currentSum = roundToCents(
        prev.reduce(
          (total, row) => total + (parseAmountInput(row.amount) ?? 0),
          0,
        ),
      );
      const remainder =
        totalAmount !== undefined
          ? roundToCents(totalAmount - currentSum)
          : undefined;
      return [
        ...prev,
        {
          key,
          // The vendor default is a starting point for the first row, not a
          // rule: later rows are a deliberate split and stay blank.
          glAccountId: prev.length === 0 ? (vendorDefaultGlAccountId ?? "") : "",
          memo: "",
          amount:
            remainder !== undefined && remainder > 0 ? String(remainder) : "",
        },
      ];
    });
    // Focus the new row's GL picker once it exists.
    requestAnimationFrame(() => {
      containerRef.current
        ?.querySelector<HTMLButtonElement>(
          `[data-allocation-row="${key}"] button[role="combobox"]`,
        )
        ?.focus();
    });
  }, [totalAmount, vendorDefaultGlAccountId]);

  const handleSave = async () => {
    if (!authUserId) return;
    // Validate every row at once — highlight all problems, not just the first.
    const invalid = new Set<number>();
    for (const row of rows) {
      const amount = parseAmountInput(row.amount);
      if (!row.glAccountId || amount === undefined || amount <= 0) {
        invalid.add(row.key);
      }
    }
    if (invalid.size > 0) {
      setInvalidKeys(invalid);
      toast.error(
        `Fix the ${invalid.size} highlighted row${invalid.size === 1 ? "" : "s"} — every allocation needs a GL account and a positive amount`,
      );
      return;
    }
    const payload = rows.map((row) => ({
      glAccountId: row.glAccountId,
      memo: row.memo.trim(),
      amount: parseAmountInput(row.amount)!,
    }));
    setIsSaving(true);
    try {
      await setAllocations({ authUserId, billId, allocations: payload });
      // Our rows now ARE the server state; the next revision-driven re-seed
      // should not read as a conflict.
      seedSignatureRef.current = rowsSignature(rows);
      setConflict(false);
      toast.success("Allocations saved");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not save allocations",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const activeAccounts = (glAccounts ?? []).filter(
    (account) => account.active,
  );
  const accountOptions = React.useMemo<ComboboxOption[]>(
    () =>
      activeAccounts.map((account) => ({
        value: account._id,
        label: account.qboAccountName,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [glAccounts],
  );
  const accountNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const account of glAccounts ?? []) {
      map.set(account._id, account.qboAccountName);
    }
    return map;
  }, [glAccounts]);

  if (!canEdit) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">GL allocations</h3>
        {allocations.length === 0 ? (
          <p className="text-muted-foreground text-sm">No allocations.</p>
        ) : (
          <div className="space-y-1 text-sm">
            {allocations.map((allocation) => (
              <div
                key={allocation._id}
                className="flex items-center justify-between gap-2 rounded border px-2 py-1"
              >
                <span className="min-w-0 truncate">
                  {allocation.glAccountName ?? "Unknown account"}
                  {allocation.memo ? (
                    <span className="text-muted-foreground">
                      {" "}
                      — {allocation.memo}
                    </span>
                  ) : null}
                </span>
                <span className="tabular-nums">
                  {formatCurrency(allocation.amount)}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between px-2 pt-1 text-xs">
              <span className="text-muted-foreground">Allocated</span>
              <span className="tabular-nums">
                {formatCurrency(
                  roundToCents(
                    allocations.reduce((total, a) => total + a.amount, 0),
                  ),
                )}
                {totalAmount !== undefined && ` of ${formatCurrency(totalAmount)}`}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2" ref={containerRef}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">GL allocations</h3>
        <Button variant="ghost" size="sm" onClick={addRow}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add row
        </Button>
      </div>

      {(() => {
        // Only rows resolved against the GL allow-list can be inserted;
        // unresolved names are surfaced as text the user must act on.
        const resolved = suggestedAllocations.filter((s) => s.glAccountId);
        const unresolved = suggestedAllocations.filter((s) => !s.glAccountId);
        if (suggestedAllocations.length === 0 || rows.length > 0) return null;
        return (
          <div className="space-y-1 rounded-md border border-dashed p-2 text-xs">
            {resolved.length > 0 && (
              <div className="flex items-center justify-between gap-2">
                <span>
                  AI suggests {resolved.length} GL line
                  {resolved.length === 1 ? "" : "s"}
                  {" — "}
                  {resolved
                    .map(
                      (s) =>
                        `${s.glAccountName.split(":").pop()}${
                          s.amount !== undefined
                            ? ` ${formatCurrency(s.amount)}`
                            : ""
                        }`,
                    )
                    .join(", ")}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 shrink-0 px-2 text-xs"
                  onClick={() =>
                    setRows(
                      resolved.map((s) => ({
                        key: nextRowKey++,
                        glAccountId: s.glAccountId!,
                        memo: s.memo,
                        amount: s.amount !== undefined ? String(s.amount) : "",
                      })),
                    )
                  }
                >
                  Use suggestion
                </Button>
              </div>
            )}
            {unresolved.length > 0 && (
              <p className="text-amber-700 dark:text-amber-400">
                Suggested account{unresolved.length === 1 ? "" : "s"} not in
                the GL list:{" "}
                {unresolved
                  .map((s) => s.glAccountName || s.memo)
                  .filter(Boolean)
                  .join(", ")}
                {" — add or pick manually."}
              </p>
            )}
          </div>
        );
      })()}

      {conflict && dirty && (
        <div className="rounded-md border px-2.5 py-1.5 text-xs">
          <span className="font-medium">
            The allocations changed elsewhere while you were editing.
          </span>{" "}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => applySeed(allocations)}
          >
            Load the latest (discards your edits)
          </button>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No allocations yet — add a row to code this bill.
        </p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((row, index) => {
            const isLast = index === rows.length - 1;
            const isInvalid = invalidKeys.has(row.key);
            return (
              <div
                key={row.key}
                data-allocation-row={row.key}
                className={cn(
                  "flex items-center gap-1.5 rounded",
                  isInvalid &&
                    "ring-destructive/60 rounded-md p-0.5 ring-1",
                )}
              >
                <ComboboxSelect
                  options={accountOptions}
                  value={row.glAccountId}
                  placeholder="GL account"
                  searchPlaceholder="Type to filter accounts…"
                  emptyText="No matching GL account."
                  loading={glAccounts === undefined}
                  className="h-8 w-56 text-xs font-normal"
                  contentClassName="w-[min(28rem,calc(100vw-2rem))]"
                  onValueChange={(value) =>
                    updateRow(row.key, { glAccountId: value })
                  }
                />
                <Input
                  value={row.memo}
                  onChange={(e) => updateRow(row.key, { memo: e.target.value })}
                  placeholder="Memo"
                  aria-label="Allocation memo"
                  className="h-8 flex-1 text-xs"
                />
                <Input
                  value={row.amount}
                  onChange={(e) => updateRow(row.key, { amount: e.target.value })}
                  onKeyDown={(e) => {
                    // Excel-style: Tab or Enter off the last amount starts the
                    // next allocation line.
                    if (
                      (e.key === "Tab" && !e.shiftKey) ||
                      e.key === "Enter"
                    ) {
                      if (isLast && !balanced && row.glAccountId) {
                        e.preventDefault();
                        addRow();
                      }
                    }
                  }}
                  placeholder="0.00"
                  type="text"
                  inputMode="decimal"
                  className="h-8 w-28 text-right text-xs tabular-nums"
                  aria-label="Allocation amount"
                  aria-invalid={isInvalid || undefined}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={`Remove allocation row ${
                    accountNameById.get(row.glAccountId) ?? index + 1
                  }`}
                  onClick={() =>
                    setRows((prev) => prev.filter((r) => r.key !== row.key))
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between border-t pt-2 text-sm">
        <span className="text-muted-foreground">
          Allocated {formatCurrency(sum)}
          {totalAmount !== undefined && ` of ${formatCurrency(totalAmount)}`}
        </span>
        <span className="flex items-center gap-3">
          {delta !== undefined && (
            <span
              className={cn(
                "text-xs font-medium tabular-nums",
                balanced
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-destructive",
              )}
            >
              {balanced
                ? "Balanced"
                : delta > 0
                  ? `${formatCurrency(delta)} over`
                  : `${formatCurrency(-delta)} left to allocate`}
            </span>
          )}
          <Button
            variant={dirty ? "destructive" : "default"}
            size="sm"
            onClick={handleSave}
            disabled={isSaving || !dirty}
          >
            {isSaving ? "Saving…" : "Save allocations"}
          </Button>
        </span>
      </div>
    </div>
  );
}
